/**
 * Agreement link builders: the one place an agreement email, page or service
 * learns the address of an agreement page.
 *
 * Billing builds every agreement email link through these (a test in
 * apps/vhire-billing bans hand-built app paths, addresses and APP_BASE links
 * there), and the frontend's route contract test
 * (v4-frontend/src/__tests__/emitted-routes-resolve.test.ts) proves that each
 * address they can emit has a page and is neither redirected nor rewritten.
 * So a path changes here, and both sides follow.
 *
 * Every builder is listed in AGREEMENT_LINK_BUILDERS. A test here fails on an
 * exported builder that is not listed, and the route contract test fails on a
 * listed builder it has no case for.
 *
 * Tenant pages (checkout, the document, the onboarding agreement) live on the
 * tenant's own origin, from tenantOrigin(). Billing passes the slug only: the
 * stored custom domain is not verified and can be stale (kb
 * trap/tenant--tenants-domain-is-unverified-and-stale). The platform queue and
 * the personal agreement link live on the apex.
 */

import { APP_BASE, tenantUrl } from './index';

/** The checkout page for one quote: `/admin/billing/checkout/{quoteId}`. It
 *  sits under the legacy '/admin/billing' redirect prefix, which the frontend
 *  middleware exempts it from. */
export const AGREEMENT_CHECKOUT_PATH = '/admin/billing/checkout';
const TENANT_AGREEMENTS_PATH = '/admin/agreements';
const ONBOARDING_AGREEMENT_PATH = '/admin/onboarding/agreement';
const PLATFORM_AGREEMENTS_PATH = '/platform-admin/agreements';
const AGREEMENT_TOKEN_PATH = '/agreement';

/** A tenant subdomain label: lowercase letters, digits and inner hyphens. */
const SLUG = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
/** A host name with at least one dot and nothing else (no path, no port). */
const HOST = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;

/** A stored custom domain as a bare host: scheme and trailing slash dropped,
 *  lowercased. Null when what is left is not a host name. */
function customHost(raw: string | null | undefined): string | null {
  const host = (raw ?? '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/+$/, '');
  return HOST.test(host) ? host : null;
}

/**
 * The origin a tenant's people open the app on: the tenant's custom domain
 * when it has a usable one, else `https://{slug}.{ROOT_DOMAIN}`, built with
 * tenantUrl(). The apex (APP_BASE) when neither is usable, so a link is never
 * built on a malformed host.
 */
export function tenantOrigin(tenant: { slug?: string | null; customDomain?: string | null } | null | undefined): string {
  const host = customHost(tenant?.customDomain);
  const slug = (tenant?.slug ?? '').trim();
  if (host) return tenantUrl({ slug, customDomain: host });
  if (SLUG.test(slug)) return tenantUrl({ slug });
  return APP_BASE;
}

/** `origin` as a bare absolute origin (no path, no trailing slash). Throws on
 *  a relative or malformed origin rather than building a relative link. */
function baseOf(origin: string): string {
  return new URL(origin).origin;
}

/** The checkout page for `quoteId` on `origin`. Throws on an empty quote id:
 *  a checkout link without one opens a page that cannot load. */
export function checkoutUrl(origin: string, quoteId: string): string {
  if (!quoteId.trim()) throw new Error('checkoutUrl needs a quote id');
  return `${baseOf(origin)}${AGREEMENT_CHECKOUT_PATH}/${encodeURIComponent(quoteId)}`;
}

/** The customer's own page for a document on `origin`. An onboarding
 *  document's tenant may not be approved yet, and an unapproved tenant can
 *  only reach pages under /admin/onboarding, so it opens the onboarding
 *  agreement page; every other document opens its own page. */
export function tenantAgreementUrl(origin: string, agreement: { id: string; origin: string }): string {
  const base = baseOf(origin);
  return agreement.origin === 'onboarding' ? `${base}${ONBOARDING_AGREEMENT_PATH}` : `${base}${TENANT_AGREEMENTS_PATH}/${encodeURIComponent(agreement.id)}`;
}

/** The platform admins' agreements queue, on the apex. */
export function platformAgreementsUrl(): string {
  return `${baseOf(APP_BASE)}${PLATFORM_AGREEMENTS_PATH}`;
}

/** A person's personal agreement link (signer, reviewer, approver), on the
 *  apex. The token is the credential: never log the result. */
export function agreementTokenUrl(token: string): string {
  return `${baseOf(APP_BASE)}${AGREEMENT_TOKEN_PATH}/${encodeURIComponent(token)}`;
}

/** Every agreement link builder. The frontend's route contract test needs a
 *  case for each one listed here, so a new builder is added here too (a test
 *  in packages/urls fails on one that is not). */
export const AGREEMENT_LINK_BUILDERS = { checkoutUrl, tenantAgreementUrl, platformAgreementsUrl, agreementTokenUrl } as const;
